import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { InteractiveHoverButton } from "@/components/ui/interactive-hover-button";
import { supabase } from "@/integrations/supabase/client";

interface FormFieldConfig {
  id: string;
  label: string;
  field_type: string;
  input_mode: string;
  max_length: number | null;
  required: boolean;
  display_order: number;
}

const defaultFields: FormFieldConfig[] = [
  { id: "nome", label: "Nome", field_type: "text", input_mode: "text", max_length: 100, required: true, display_order: 1 },
  { id: "email", label: "Email", field_type: "email", input_mode: "email", max_length: 255, required: false, display_order: 2 },
  { id: "empresa", label: "Empresa", field_type: "text", input_mode: "text", max_length: 100, required: false, display_order: 3 },
  { id: "whatsapp", label: "Whatsapp", field_type: "tel", input_mode: "tel", max_length: 20, required: true, display_order: 4 },
  { id: "mensagem", label: "Mensagem", field_type: "textarea", input_mode: "text", max_length: 1000, required: true, display_order: 5 },
];

const ContactForm = () => {
  const [fields, setFields] = useState<FormFieldConfig[]>(defaultFields);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchFields = async () => {
      const { data } = await supabase
        .from("contact_form_fields")
        .select("*")
        .order("display_order");
      if (data && data.length > 0) {
        setFields(data as FormFieldConfig[]);
        // Initialize form data
        const initial: Record<string, string> = {};
        data.forEach((f: any) => { initial[f.label] = ""; });
        setFormData(initial);
      } else {
        const initial: Record<string, string> = {};
        defaultFields.forEach((f) => { initial[f.label] = ""; });
        setFormData(initial);
      }
    };
    fetchFields();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const { error: fnError } = await supabase.functions.invoke("send-contact-email", {
        body: formData,
      });
      if (fnError) throw fnError;
      setSubmitted(true);
    } catch (err: any) {
      console.error("Erro ao enviar:", err);
      setError("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (label: string, value: string) => {
    setFormData({ ...formData, [label]: value });
  };

  return (
    <section className="relative pb-0" style={{ backgroundColor: '#f9f9f9' }}>
      <div className="max-w-3xl mx-auto px-4 pt-20">
        <h2 className="text-4xl md:text-5xl font-bold text-[#272727] text-center mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Vamos conversar?
        </h2>
        <div className="flex justify-center mb-6">
          <div className="w-12 h-1 rounded-full" style={{ background: '#ffbc00' }} />
        </div>
        <p className="text-[#555] text-center mb-12 max-w-xl mx-auto text-sm leading-relaxed">
          Preencha o formulário e nossa equipe comercial entrará em contato com a melhor solução para o seu negócio.
        </p>

        <div className="relative">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="space-y-5 pb-20 max-w-xl mx-auto"
          >
            {submitted ? (
              <div className="text-center py-12">
                <p className="text-[#ffbc00] text-xl font-semibold mb-2">Mensagem enviada com sucesso!</p>
                <p className="text-[#666] text-sm">Em breve entraremos em contato.</p>
              </div>
            ) : (
              <>
                {fields.map((field) => (
                  <div key={field.id}>
                    <label htmlFor={`field-${field.id}`} className="text-[#555] text-sm mb-1 block">
                      {field.label} {field.required && <span className="text-red-400">*</span>}
                    </label>
                    {field.field_type === "textarea" ? (
                      <textarea
                        id={`field-${field.id}`}
                        rows={5}
                        required={field.required}
                        maxLength={field.max_length || undefined}
                        value={formData[field.label] || ""}
                        onChange={(e) => updateField(field.label, e.target.value)}
                        className="w-full bg-white border border-[#ddd] rounded-lg px-4 py-3 text-[#272727] placeholder:text-[#bbb] focus:outline-none focus:ring-2 focus:ring-[#ffbc00]/50 resize-none"
                      />
                    ) : (
                      <input
                        id={`field-${field.id}`}
                        type={field.field_type}
                        inputMode={field.input_mode as any}
                        required={field.required}
                        maxLength={field.max_length || undefined}
                        value={formData[field.label] || ""}
                        onChange={(e) => updateField(field.label, e.target.value)}
                        className="w-full bg-white border border-[#ddd] rounded-lg px-4 py-3 text-[#272727] placeholder:text-[#bbb] focus:outline-none focus:ring-2 focus:ring-[#ffbc00]/50"
                      />
                    )}
                  </div>
                ))}
                {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                <div className="flex justify-center">
                  <InteractiveHoverButton
                    text={loading ? "Enviando..." : "Enviar"}
                    className="border-[#ffbc00] text-[#272727]"
                  />
                </div>
              </>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
